// src/pages/DocumentsDashboard.jsx
import React, { useEffect, useState, useRef } from "react";
import { auth } from "../firebase-config";
import Sidebar from "../components/ui/Sidebar";
import PageHeader from "../components/ui/PageHeader";
import CardGridComponent from "../components/ui/CardGridComponent";
import ListCard from "../components/ui/cards/ListCard";
import ButtonComponent from "../components/ui/ButtonComponent";
import ClientDocumentCard from "../components/ui/cards/ClientDocumentCard";
import AssignToClientModal from "../components/ui/AssignToClientModal";
import { useSidebar } from "../context/SidebarContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Box, Typography, Paper, Divider, Checkbox } from "@mui/material";
import { useToast } from "../components/ui/ToastContext";
import ModalComponent from "../components/ui/ModalComponent";
import CreateClientModal from "../components/ui/CreateClientModal";
import AssignFormsModal from "../components/ui/AssignFormsModal";
import InviteClientModal from "../components/ui/InviteClientModal";
import DocsDashboardSkeleton from "../components/ui/DocsDashboardSkeleton";

const DASHBOARD_CARD_LIMIT = 8;

const LIST_CLIENTS_LITE_URL = "https://listclientslite-774073159618.us-central1.run.app";
const CLIENTS_LITE_PAGE_SIZE = 75;

const RECENTLY_ADDED_BADGE_MS = 6000;

const DocumentsDashboard = () => {
  const { isSidebarCollapsed } = useSidebar();
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [allClients, setAllClients] = useState([]);
  const [clientsCursor, setClientsCursor] = useState(null);
  const [clientsHasMore, setClientsHasMore] = useState(true);
  const [clientsLoadingMore, setClientsLoadingMore] = useState(false);

  const [loading, setLoading] = useState(true);

  const [recentDocuments, setRecentDocuments] = useState([]);
  const [oneTimeUploads, setOneTimeUploads] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignPayload, setReassignPayload] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [docToRemove, setDocToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [createClientModalOpen, setCreateClientModalOpen] = useState(false);

  const [refreshingClientId, setRefreshingClientId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearching, setClientSearching] = useState(false);
  const debounceRef = useRef(null);

  const [assignFormsOpen, setAssignFormsOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [activeClient, setActiveClient] = useState(null);
  const [assigningForms, setAssigningForms] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // NEW: cross-section document selection state
  const [docSelectMode, setDocSelectMode] = useState(false);
  const [selectedDocKeys, setSelectedDocKeys] = useState([]); // [{source:'recent'|'oneTime', key:string}]
  const [assignSelectedOpen, setAssignSelectedOpen] = useState(false);

  const [hiddenCards, setHiddenCards] = useState(
    JSON.parse(localStorage.getItem("hiddenCards")) || []
  );

  // NEW: pinned (recently added via search) ordering + badge state
  const [pinnedClientIds, setPinnedClientIds] = useState([]); // most recent first
  const [pinnedClientCache, setPinnedClientCache] = useState({}); // id -> client object
  const [recentlyAddedById, setRecentlyAddedById] = useState({}); // id -> timestamp (ms)

  const navigate = useNavigate();
  const { addToast } = useToast();
  const organizationId = sessionStorage.getItem("organizationId");

  const assignSelectedLockRef = useRef(false);
  const assignSelectedRequestIdRef = useRef(null);
  

  const updateClientInState = (updatedClient) => {
    const updater = (list) =>
      (list || []).map((c) => (c.id === updatedClient.id ? { ...c, ...updatedClient } : c));
    setClients(updater);
    setFiltered(updater);
  };

  const pinClientToTop = (clientObj) => {
    if (!clientObj?.id) return;

    const id = clientObj.id;
    const now = Date.now();

    setPinnedClientIds((prev) => [id, ...prev.filter((x) => x !== id)]);
    setPinnedClientCache((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...clientObj } }));
    setRecentlyAddedById((prev) => ({ ...prev, [id]: now }));

    window.setTimeout(() => {
      setRecentlyAddedById((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }, RECENTLY_ADDED_BADGE_MS);
  };

  const applyPinnedOrdering = (serverClients = []) => {
    const byId = new Map();
    (serverClients || []).forEach((c) => c?.id && byId.set(c.id, c));

    const pinned = pinnedClientIds
      .map((id) => {
        const cached = pinnedClientCache[id];
        const server = byId.get(id);
        if (cached && server) return { ...cached, ...server };
        return cached || server || null;
      })
      .filter(Boolean);

    const rest = (serverClients || []).filter((c) => c?.id && !pinnedClientIds.includes(c.id));

    return [...pinned, ...rest].slice(0, DASHBOARD_CARD_LIMIT);
  };

  // Debounced client search
  useEffect(() => {
    if (!clientSearchTerm || clientSearchTerm.length < 2) {
      setClientSearchResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      try {
        setClientSearching(true);
        const idToken = await auth.currentUser.getIdToken(true);
        const res = await axios.post(
          "https://searchclientstest-774073159618.us-central1.run.app",
          { query: clientSearchTerm },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        setClientSearchResults(res.data.clients || []);
      } catch (e) {
        console.error("Client search failed:", e);
        setClientSearchResults([]);
      } finally {
        setClientSearching(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [clientSearchTerm]);

  const handleClientSearchSelection = async (client) => {
    if (!client || !client.id) return;

    // Pin it to the top for this session + trigger badge
    pinClientToTop(client);

    // Ensure visible cards show it immediately at the top
    setClients((prev) => applyPinnedOrdering([client, ...(prev || [])]));
    setFiltered((prev) => applyPinnedOrdering([client, ...(prev || [])]));

    addToast(`${client.fullName} has been added to the dashboard.`, "info");

    setClientSearchTerm("");
    setClientSearchResults([]);

    // Fetch enriched client immediately so docs/submissions show without manual refresh
    try {
      setRefreshingClientId(client.id);
      const token = await auth.currentUser.getIdToken(true);

      const res = await axios.post(
        "https://refreshclientdata-774073159618.us-central1.run.app",
        { clientId: client.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const enriched = res?.data?.client;
      if (enriched?.id) {
        pinClientToTop(enriched);
        setClients((prev) => applyPinnedOrdering([enriched, ...(prev || [])]));
        setFiltered((prev) => applyPinnedOrdering([enriched, ...(prev || [])]));
      }
    } catch (e) {
      console.error("Failed to auto-fetch client documents after search selection:", e);
    } finally {
      setRefreshingClientId(null);
    }
  };

  const fetchClients = async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch("https://getdashboarddata-774073159618.us-central1.run.app", {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ type: "documents" }),
      });
      const result = await response.json();

      const resultClients = result.clients || [];
      const ordered = applyPinnedOrdering(resultClients);

      setClients(ordered);
      setFiltered(ordered);
    } catch (error) {
      console.error("Error fetching client data:", error);
    } finally {
      if (initial) setLoading(false);
    }
  };

  const mergeUniqueClientsById = (prev, next) => {
    const map = new Map();
    (prev || []).forEach((c) => c?.id && map.set(c.id, c));
    (next || []).forEach((c) => c?.id && map.set(c.id, c));
    return Array.from(map.values());
  };

  const fetchClientsLitePage = async ({ reset = false } = {}) => {
    if (clientsLoadingMore) return;
    if (!reset && !clientsHasMore) return;

    setClientsLoadingMore(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const payload = {
        limit: CLIENTS_LITE_PAGE_SIZE,
        cursor: reset ? null : clientsCursor,
      };

      const res = await axios.post(LIST_CLIENTS_LITE_URL, payload, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      const newClients = res.data?.clients || [];
      const nextCursor = res.data?.nextCursor || null;
      const hasMore = !!res.data?.hasMore;

      setAllClients((prev) => (reset ? newClients : mergeUniqueClientsById(prev, newClients)));
      setClientsCursor(nextCursor);
      setClientsHasMore(hasMore);
    } catch (e) {
      console.error("listClientsLite failed:", e);
      setClientsHasMore(false);
    } finally {
      setClientsLoadingMore(false);
    }
  };

  const fetchRecentDocuments = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch("https://getrecentdocuments-774073159618.us-central1.run.app", {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const result = await response.json();
      setRecentDocuments(result.documents || []);
    } catch (error) {
      console.error("Error fetching recent documents:", error);
    }
  };

  const fetchOneTimeUploads = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const response = await fetch("https://getonetimedocuments-774073159618.us-central1.run.app", {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ organizationId }),
      });
      const result = await response.json();
      setOneTimeUploads(result.documents || []);
    } catch (error) {
      console.error("Error fetching one-time uploads:", error);
    }
  };

  const fetchFormTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch("https://getformtemplates-774073159618.us-central1.run.app", {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = await response.json();
      setFormTemplates(data.templates || []);
    } catch (error) {
      console.error("Error fetching form templates:", error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    fetchClients(true);
    fetchRecentDocuments();
    fetchOneTimeUploads();
    fetchFormTemplates();
    fetchClientsLitePage({ reset: true });
  }, []);

  useEffect(() => {
    const modalOpen = assignSelectedOpen || assignModalOpen || reassignModalOpen;
    if (modalOpen && allClients.length === 0) {
      fetchClientsLitePage({ reset: true });
    }
  }, [assignSelectedOpen, assignModalOpen, reassignModalOpen]);

  const hideCard = (cardName) => {
    const updated = [...hiddenCards, cardName];
    setHiddenCards(updated);
    localStorage.setItem("hiddenCards", JSON.stringify(updated));
  };

  const handleClientRefresh = async (clientId) => {
    if (refreshingClientId) return;
    try {
      setRefreshingClientId(clientId);
      const token = await auth.currentUser.getIdToken(true);
      const res = await axios.post(
        "https://refreshclientdata-774073159618.us-central1.run.app",
        { clientId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // keep pinned ordering even after refresh
      const refreshed = res.data.client;
      if (refreshed?.id && pinnedClientIds.includes(refreshed.id)) {
        setPinnedClientCache((prev) => ({ ...prev, [refreshed.id]: { ...(prev[refreshed.id] || {}), ...refreshed } }));
      }

      updateClientInState(refreshed);
    } catch (error) {
      console.error("Error refreshing client:", error);
    } finally {
      setRefreshingClientId(null);
    }
  };

  const handleUploadDashboardDocument = async (file) => {
    setIsUploading(true);
    document.body.style.cursor = "wait";
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const base64Data = reader.result.split(",")[1];
            await axios.post(
              "https://uploaddashboarddocument-774073159618.us-central1.run.app",
              { documentBase64: base64Data, fileName: file.name, organizationId },
              { headers: { Authorization: `Bearer ${idToken}` } }
            );
            resolve();
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });

      addToast("Document uploaded successfully", "success");
      await fetchClients(false);
      await fetchRecentDocuments();
    } catch (err) {
      console.error("Upload error:", err);
      addToast("Failed to upload document", "error");
    } finally {
      setIsUploading(false);
      document.body.style.cursor = "default";
    }
  };

  const handleRemoveAssignedDocument = async (clientId, docId) => {
    if (!clientId || !docId) {
      addToast("Error: Missing information to remove document.", "error");
      return;
    }
    setIsRemoving(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      await axios.post(
        "https://removeassigneddocument-774073159618.us-central1.run.app",
        { clientId, docId },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      addToast("Document removed", "success");
      await fetchClients(false);
    } catch (err) {
      console.error("Failed to remove assigned document:", err);
      addToast("Failed to remove document", "error");
    } finally {
      setIsRemoving(false);
      setDocToRemove(null);
    }
  };

  const handleAssignClick = (doc) => {
    setSelectedDocument(doc);
    setAssignModalOpen(true);
  };

  const handleMoveToOneTime = async (doc) => {
    try {
      const idToken = await auth.currentUser.getIdToken(true);
      await axios.post(
        "https://movedocumenttoonetimeupload-774073159618.us-central1.run.app",
        { path: doc.path, name: doc.name, organizationId },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      addToast("Moved to One-Time Uploads", "success");
      fetchRecentDocuments();
      fetchOneTimeUploads();
    } catch (err) {
      console.error("Move failed:", err);
      addToast("Failed to move document", "error");
    }
  };

  const setReassignPayloadOpen = (originalId, documentId, documentData) => {
    setReassignPayload({ originalId, documentId, documentData });
    setReassignModalOpen(true);
  };

  const handleAssignSelectedDocumentsToClients = async (
    clientIds,
    notifyOptions = {}
  ) => {
    // Hard guard against duplicate invocations (double click / Enter / modal double-fire)
    if (assignSelectedLockRef.current) return;
    if (isSubmitting) return;

    if (!Array.isArray(clientIds) || clientIds.length === 0) {
      addToast("Please select at least one client.", "warning");
      return;
    }
    if (!Array.isArray(selectedDocKeys) || selectedDocKeys.length === 0) {
      addToast("No documents selected.", "warning");
      return;
    }

    const { sendNotification = false, channel = "email" } = notifyOptions || {};

    const buildDocIndexes = (recentDocs = [], oneTimes = []) => {
      const recentByPath = new Map();
      const recentById = new Map();
      const recentLegacy = new Map();

      for (const d of Array.isArray(recentDocs) ? recentDocs : []) {
        const id = d?.id || d?.docId;
        const path = d?.path || null;
        const legacy = `${d?.name || ""}|${d?.uploaded?._seconds ?? ""}`;

        if (path) {
          recentByPath.set(`recent:${path}`, d);
          recentByPath.set(path, d); // back-compat
        }
        if (id) {
          recentById.set(`recentId:${id}`, d);
          recentById.set(id, d); // back-compat
        }
        recentLegacy.set(`recentLegacy:${legacy}`, d);
        recentLegacy.set(legacy, d); // back-compat
      }

      const oneTimeById = new Map();
      for (const d of Array.isArray(oneTimes) ? oneTimes : []) {
        const id = d?.id || d?.docId;
        if (!id) continue;
        oneTimeById.set(`oneTime:${id}`, d);
        oneTimeById.set(id, d); // back-compat
      }

      return { recentByPath, recentById, recentLegacy, oneTimeById };
    };

    const resolveSelectedDoc = (source, key, idx) => {
      if (!key) return null;

      if (source === "oneTime") {
        const found =
          idx.oneTimeById.get(key) || idx.oneTimeById.get(`oneTime:${key}`);
        return found ? { found, sourceType: "oneTime" } : null;
      }

      const found =
        idx.recentByPath.get(key) ||
        idx.recentById.get(key) ||
        idx.recentLegacy.get(key);

      return found ? { found, sourceType: "recent" } : null;
    };

    // base64url encoder (no '/', '+', '=')
    const toBase64Url = (str) => {
      const b64 = btoa(unescape(encodeURIComponent(String(str))));
      return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
    };

    // safe basename for paths (removes slashes)
    const basename = (p) => {
      if (!p) return "";
      const s = String(p);
      const parts = s.split("/");
      return parts[parts.length - 1] || "";
    };

    assignSelectedLockRef.current = true;
    setIsSubmitting(true);

    try {
      const idToken = await auth.currentUser.getIdToken(true);
      const idx = buildDocIndexes(recentDocuments, oneTimeUploads);

      const docsToAssign = selectedDocKeys
        .map(({ source, key }) => {
          const resolved = resolveSelectedDoc(source, key, idx);
          if (!resolved) return null;

          const { found, sourceType } = resolved;

          if (sourceType === "oneTime") {
            return {
              id: found.id || found.docId,
              source: "one-time",
              name: found.name,
              uploaded: found.uploaded,
              downloadUrl: found.downloadUrl || found.url || null,
              url: found.url || found.downloadUrl || null,
              path: found.path || null,
            };
          }

          return {
            name: found.name,
            path: found.path || null,
            source: "dashboard",
            uploaded: found.uploaded,
            downloadUrl: found.downloadUrl || found.url || null,
            url: found.url || found.downloadUrl || null,
          };
        })
        .filter(Boolean);

      if (docsToAssign.length === 0) {
        addToast("Could not resolve selected documents. Please try again.", "error");
        return;
      }

      // Create ONE stable requestId per “Assign Selected” attempt.
      // If the modal double-fires, it will reuse the same id and dedupe will work.
      if (!assignSelectedRequestIdRef.current) {
        assignSelectedRequestIdRef.current =
          (globalThis.crypto && typeof globalThis.crypto.randomUUID === "function")
            ? globalThis.crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }

      // Build a dedupe key that is:
      // - stable for this user action
      // - does NOT contain slashes
      // - reasonably short
      const stableDocKeys = docsToAssign
        .map((d) => {
          if (d.source === "one-time") return `oneTime:${d.id || ""}`;
          return `recent:${basename(d.path)}:${d.name || ""}`;
        })
        .sort();

      const rawNotificationKey = [
        "assignSelected",
        organizationId,
        channel === "sms" || channel === "text" ? "sms" : "email",
        sendNotification ? "notify" : "silent",
        assignSelectedRequestIdRef.current,
        ...[...clientIds].sort(),
        ...stableDocKeys,
      ].join("|");

      const notificationKey = toBase64Url(rawNotificationKey);

      await axios.post(
        "https://assigndocumenttoclients-774073159618.us-central1.run.app",
        {
          organizationId,
          documents: docsToAssign,
          clientIds,
          sendNotification,
          channel,
          notificationKey,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      addToast("Selected documents assigned successfully.", "success");

      // Reset UI + reset request id (next assign is a new action)
      assignSelectedRequestIdRef.current = null;

      setSelectedDocKeys([]);
      setDocSelectMode(false);
      setAssignSelectedOpen(false);
      await fetchClients(false);
    } catch (error) {
      console.error("Batch assignment failed:", error);
      addToast(
        error?.response?.data?.error || "Failed to assign selected documents",
        "error"
      );
    } finally {
      setIsSubmitting(false);
      assignSelectedLockRef.current = false;
    }
  };

  const handleAssignToClients = async (clientIds, notifyOptions = {}) => {
    if (isSubmitting) return;

    if (!Array.isArray(clientIds) || clientIds.length === 0) {
      addToast("Please select at least one client.", "warning");
      return;
    }

    if (!selectedDocument) {
      addToast("No document selected to assign.", "warning");
      return;
    }

    const { sendNotification = false, channel = "email" } = notifyOptions || {};

    setIsSubmitting(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      // Keep single-doc flow, but send as documents[] for consistency (CF supports both)
      await axios.post(
        "https://assigndocumenttoclients-774073159618.us-central1.run.app",
        {
          organizationId,
          documents: [selectedDocument], // ✅ consistent
          clientIds,
          sendNotification,
          channel,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      addToast("Document assigned successfully", "success");
      await fetchClients(false);
    } catch (error) {
      console.error("Assignment failed:", error);
      addToast(error?.response?.data?.error || "Failed to assign document", "error");
    } finally {
      setIsSubmitting(false);
      setAssignModalOpen(false);
      setSelectedDocument(null);
    }
  };

  const confirmReassignment = async (newClientIds, notifyOptions = {}) => {
    if (isSubmitting) return;
    if (!reassignPayload || !newClientIds || newClientIds.length === 0) return;

    const { sendNotification = false, channel = "email" } = notifyOptions || {};

    setIsSubmitting(true);
    try {
      const idToken = await auth.currentUser.getIdToken(true);

      const reassignmentPromises = newClientIds.map((newId) =>
        axios.post(
          "https://reassigndashboarddocument-774073159618.us-central1.run.app",
          {
            documentId: reassignPayload.documentId,
            originalClientId: reassignPayload.originalId,
            newClientId: newId,
            organizationId,
            documentData: reassignPayload.documentData,
            sendNotification,
            channel,
          },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "x-user-email": auth.currentUser.email,
            },
          }
        )
      );

      await Promise.all(reassignmentPromises);
      addToast("Document reassigned successfully", "success");
      await fetchClients(false);
    } catch (err) {
      console.error("Failed to reassign document:", err);
      addToast("Failed to reassign document", "error");
    } finally {
      setIsSubmitting(false);
      setReassignModalOpen(false);
      setReassignPayload(null);
    }
  };

  const handleRequestNewClient = () => setCreateClientModalOpen(true);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUploadDashboardDocument(file);
  };

  const handleOpenAssignForms = (client) => {
    setActiveClient(client);
    setAssignFormsOpen(true);
  };
  const handleOpenInvite = (client) => {
    setActiveClient(client);
    setInviteOpen(true);
  };

  const keyForRecent = (doc) => {
    if (doc?.path) return `recent:${doc.path}`;
    if (doc?.id || doc?.docId) return `recentId:${doc.id || doc.docId}`;
    return `recentLegacy:${doc?.name || ""}|${doc?.uploaded?._seconds ?? ""}`;
  };

  const keyForOneTime = (doc) => `oneTime:${doc.id || doc.docId}`;

  const isSelected = (source, key) => selectedDocKeys.some((k) => k.source === source && k.key === key);

  const toggleSelect = (source, key) => {
    setSelectedDocKeys((prev) =>
      isSelected(source, key)
        ? prev.filter((k) => !(k.source === source && k.key === key))
        : [...prev, { source, key }]
    );
  };

  const clientCards = filtered
    .filter((client) => !hiddenCards.includes(client.email))
    .map((client, index) => ({
      id: client.id || `client-${index}`,
      size: "medium",
      shape: "rectangle",
      isDraggable: false,
      content: (
        <ClientDocumentCard
          client={client}
          isRefreshing={refreshingClientId === client.id}
          isRecentlyAdded={!!recentlyAddedById[client.id]}
          onRefresh={() => handleClientRefresh(client.id)}
          onHide={hideCard}
          hideKey={client.email}
          onRemoveRequest={(docId) => setDocToRemove({ clientId: client.id, docId })}
          onReassignClick={(doc) =>
            setReassignPayloadOpen(client.id, doc.docId || doc.id, {
              ...doc,
              assignedAt: new Date().toISOString(),
              assignedBy: auth.currentUser.email,
            })
          }
          onAssignForms={handleOpenAssignForms}
          onInviteClient={handleOpenInvite}
        />
      ),
    }));

  const submitAssignForms = async ({ clientId, formIds, dueDate, note, sendInvite, channel }) => {
    if (!clientId || !Array.isArray(formIds) || formIds.length === 0) return;
    try {
      setAssigningForms(true);
      const idToken = await auth.currentUser.getIdToken(true);

      const orgId = sessionStorage.getItem("organizationId") || undefined;
      const payload = {
        orgId,
        clientId,
        forms: { ids: formIds },
        dueDate: dueDate || null,
        note: note || "",
        notify: !!sendInvite,
        channel,
      };

      const { data } = await axios.post("https://assigntoclient-774073159618.us-central1.run.app", payload, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      addToast(`Forms assigned (${data.assignedCount})`, "success");
      await handleClientRefresh(clientId);
    } catch (e) {
      console.error("assign forms failed:", e);
      addToast(e?.response?.data?.error || "Failed to assign forms.", "error");
    } finally {
      setAssigningForms(false);
      setAssignFormsOpen(false);
      setActiveClient(null);
    }
  };

  const sendInviteOnly = async ({ clientId, channel, message }) => {
    if (!clientId) return;
    try {
      setSendingInvite(true);
      const idToken = await auth.currentUser.getIdToken(true);
      await axios.post(
        "https://createportalinvite-774073159618.us-central1.run.app",
        { clientId, channel, message },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      addToast("Invite sent.", "success");
    } catch (e) {
      console.error("invite failed:", e);
      addToast("Failed to send invite.", "error");
    } finally {
      setSendingInvite(false);
      setInviteOpen(false);
      setActiveClient(null);
    }
  };

return (
  <Box
    sx={{
      "@keyframes kinovaFadeOut": {
        "0%": { opacity: 0, transform: "translateY(-2px)" },
        "10%": { opacity: 1, transform: "translateY(0)" },
        "75%": { opacity: 1, transform: "translateY(0)" },
        "100%": { opacity: 0, transform: "translateY(-2px)" },
      },
    }}
  >
    <div className="manage-users-container">
      <Sidebar />
      <div className={`manage-users-content ${isSidebarCollapsed ? "expanded" : ""}`}>
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            width: "100%",
            outline: isDragOver ? "3px dashed #3f51b5" : "none",
            backgroundColor: isDragOver ? "rgba(63,81,181,0.05)" : "transparent",
            transition: "background-color .2s ease, outline .2s ease",
            pb: 4, // space under last dashboard card
          }}
        >
          <PageHeader
            title="Documents Dashboard"
            showSearch
            searchTerm={clientSearchTerm}
            onSearch={setClientSearchTerm}
            results={clientSearchResults}
            isSearching={clientSearching}
            placeholder="Search clients..."
            onSelectResult={handleClientSearchSelection}
            renderResult={(c) => (
              <>
                <strong>{c.fullName}</strong>
                <div className="search-meta">{c.email}</div>
              </>
            )}
          />

          {/* ✅ spacing AFTER header (this is the top padding you wanted) */}
          <Box sx={{ mt: 2 }}>
            {loading ? (
              <Box>
                <DocsDashboardSkeleton />
              </Box>
            ) : (
              <>
                <CardGridComponent cards={clientCards} isSidebarCollapsed={isSidebarCollapsed} />

                {/* Recent Uploaded Documents */}
                <Paper elevation={0} sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">Recent Uploaded Documents</Typography>
                    <Box display="flex" gap={1}>
                      {!docSelectMode ? (
                        <ButtonComponent
                          label="Select documents"
                          type="secondary"
                          size="small"
                          onClick={() => setDocSelectMode(true)}
                        />
                      ) : (
                        <ButtonComponent
                          label="Cancel selection"
                          type="secondary"
                          size="small"
                          onClick={() => {
                            setDocSelectMode(false);
                            setSelectedDocKeys([]);
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <ListCard
                    inset
                    items={recentDocuments}
                    emptyMessage="No recent documents found"
                    renderItem={(doc) => {
                      const key = keyForRecent(doc);
                      const checked = docSelectMode && isSelected("recent", key);
                      return (
                        <div className="flex justify-between items-center w-full">
                          <div className="flex items-center gap-2 flex-1">
                            {docSelectMode && (
                              <Checkbox
                                size="small"
                                checked={!!checked}
                                onChange={() => toggleSelect("recent", key)}
                              />
                            )}
                            <div>
                              <div className="font-semibold">{doc.name || "Unnamed Document"}</div>
                              <div className="text-xs text-gray-500">
                                {doc.uploaded
                                  ? new Date(doc.uploaded._seconds * 1000).toLocaleString()
                                  : "Unknown date"}
                              </div>
                            </div>
                          </div>

                          {!docSelectMode && (
                            <div className="flex gap-2">
                              <ButtonComponent
                                label="Assign"
                                onClick={() => handleAssignClick(doc)}
                                type="secondary"
                                size="small"
                              />
                              <ButtonComponent
                                label="Move to One-Time"
                                onClick={() => handleMoveToOneTime(doc)}
                                type="secondary"
                                size="small"
                              />
                            </div>
                          )}
                        </div>
                      );
                    }}
                  />
                </Paper>

                {/* One-Time Uploads */}
                <Paper elevation={0} sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">One-Time Uploads</Typography>
                    <Box display="flex" gap={1}>
                      {!docSelectMode ? (
                        <ButtonComponent
                          label="Select documents"
                          type="secondary"
                          size="small"
                          onClick={() => setDocSelectMode(true)}
                        />
                      ) : (
                        <ButtonComponent
                          label="Cancel selection"
                          type="secondary"
                          size="small"
                          onClick={() => {
                            setDocSelectMode(false);
                            setSelectedDocKeys([]);
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <ListCard
                    inset
                    items={oneTimeUploads}
                    emptyMessage="No one-time uploads yet"
                    renderItem={(doc) => {
                      const key = keyForOneTime(doc);
                      const checked = docSelectMode && isSelected("oneTime", key);
                      return (
                        <div className="flex justify-between items-center w-full">
                          <div className="flex items-center gap-2 flex-1">
                            {docSelectMode && (
                              <Checkbox
                                size="small"
                                checked={!!checked}
                                onChange={() => toggleSelect("oneTime", key)}
                              />
                            )}
                            <div>
                              <div className="font-semibold">{doc.name || "Unnamed Document"}</div>
                              <div className="text-xs text-gray-500">
                                {doc.uploaded
                                  ? new Date(doc.uploaded._seconds * 1000).toLocaleString()
                                  : "Unknown date"}
                              </div>
                            </div>
                          </div>

                          {!docSelectMode && (
                            <ButtonComponent
                              label="Assign"
                              onClick={() => handleAssignClick(doc)}
                              type="secondary"
                              size="small"
                            />
                          )}
                        </div>
                      );
                    }}
                  />
                </Paper>

                {/* Form Templates */}
                <Paper elevation={0} sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">Published Form Templates</Typography>
                    <ButtonComponent
                      label="Go to Form Builder"
                      type="secondary"
                      size="small"
                      onClick={() => navigate("/documents/builder")}
                    />
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {(() => {
                    const intakeShortcut = {
                      _isIntakeShortcut: true,
                      name: "Client Intake & Pre-Qualification Form",
                      description: "Direct link to the built-in intake form",
                    };

                    const nonIntakeTemplates = (Array.isArray(formTemplates) ? formTemplates : []).filter((t) => {
                      const title = (t?.name || t?.title || "").trim();
                      return !/client\s*intake.*pre[-\s]?qual/i.test(title);
                    });

                    const items = [intakeShortcut, ...nonIntakeTemplates];

                    return (
                      <ListCard
                        inset
                        items={items}
                        emptyMessage={templatesLoading ? "Loading templates..." : "No published form templates found"}
                        renderItem={(template) => {
                          const isIntakeShortcut = !!template._isIntakeShortcut;

                          const updatedDate = template.updated?.toDate
                            ? template.updated.toDate().toLocaleString()
                            : template.updated?._seconds
                            ? new Date(template.updated._seconds * 1000).toLocaleString()
                            : undefined;

                          return (
                            <div className="flex justify-between items-center w-full">
                              <div>
                                <div className="font-semibold">
                                  {template.name || template.title || "Untitled Form"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {isIntakeShortcut
                                    ? "Built-in form — no template required"
                                    : template.description || (updatedDate ? `Updated: ${updatedDate}` : " ")}
                                </div>
                              </div>

                              <ButtonComponent
                                label="Fill Form"
                                type="secondary"
                                size="small"
                                onClick={() =>
                                  isIntakeShortcut
                                    ? navigate("/documents/client-intake")
                                    : navigate(`/forms/fill/${template.id}`)
                                }
                              />
                            </div>
                          );
                        }}
                      />
                    );
                  })()}
                </Paper>

                {/* Global selection action bar */}
                {docSelectMode && selectedDocKeys.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      mt: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      background: "#fafafa",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      zIndex: 1,
                    }}
                  >
                    <Typography variant="body2">
                      {selectedDocKeys.length} document{selectedDocKeys.length > 1 ? "s" : ""} selected
                    </Typography>
                    <Box display="flex" gap={1}>
                      <ButtonComponent
                        label="Assign selected…"
                        type="primary"
                        size="small"
                        onClick={() => setAssignSelectedOpen(true)}
                      />
                      <ButtonComponent
                        label="Clear"
                        type="secondary"
                        size="small"
                        onClick={() => setSelectedDocKeys([])}
                      />
                    </Box>
                  </Paper>
                )}
              </>
            )}
          </Box>
        </Box>
      </div>

      {/* Assign Forms Modal */}
      {assignFormsOpen && (
        <AssignFormsModal
          open={assignFormsOpen}
          onClose={() => !assigningForms && setAssignFormsOpen(false)}
          onSubmit={submitAssignForms}
          isSubmitting={assigningForms}
          client={activeClient}
          templates={formTemplates}
          bundles={bundles}
          allowBundles
        />
      )}

      {/* Invite Modal */}
      {inviteOpen && (
        <InviteClientModal
          open={inviteOpen}
          onClose={() => !sendingInvite && setInviteOpen(false)}
          onSend={sendInviteOnly}
          isSending={sendingInvite}
          client={activeClient}
        />
      )}

      {/* Assign *selected docs* to clients */}
      {assignSelectedOpen && (
        <AssignToClientModal
          open={assignSelectedOpen}
          isSubmitting={isSubmitting}
          title="Assign Selected Documents to Clients"
          clients={allClients}
          hasMore={clientsHasMore}
          isLoadingMore={clientsLoadingMore}
          onLoadMore={() => fetchClientsLitePage({ reset: false })}
          onCreateNewClient={handleRequestNewClient}
          onClose={() => !isSubmitting && setAssignSelectedOpen(false)}
          onSelect={(selectedClients, notifyOptions) => {
            if (isSubmitting) return;
            const ids = selectedClients.map((c) => c.id);
            handleAssignSelectedDocumentsToClients(ids, notifyOptions);
          }}
        />
      )}

      {(assignModalOpen || reassignModalOpen) && (
        <AssignToClientModal
          open={assignModalOpen || reassignModalOpen}
          isSubmitting={isSubmitting}
          onClose={() => {
            if (isSubmitting) return;
            setAssignModalOpen(false);
            setReassignModalOpen(false);
            setSelectedDocument(null);
            setReassignPayload(null);
          }}
          title={assignModalOpen ? "Assign Document to Clients" : "Reassign Document"}
          clients={allClients}
          hasMore={clientsHasMore}
          isLoadingMore={clientsLoadingMore}
          onLoadMore={() => fetchClientsLitePage({ reset: false })}
          excludeId={reassignPayload?.originalId}
          onCreateNewClient={handleRequestNewClient}
          onSelect={(selectedClients, notifyOptions) => {
            const selectedIds = selectedClients.map((c) => c.id);
            if (assignModalOpen) handleAssignToClients(selectedIds, notifyOptions);
            else confirmReassignment(selectedIds, notifyOptions);
          }}
        />
      )}

      {createClientModalOpen && (
        <CreateClientModal
          open={createClientModalOpen}
          onClose={() => {
            setCreateClientModalOpen(false);
            setAssignModalOpen(true);
          }}
          onClientCreated={async (newClient) => {
            setCreateClientModalOpen(false);
            if (newClient) {
              pinClientToTop(newClient);

              const updated = applyPinnedOrdering([newClient, ...clients]);
              setClients(updated);
              setFiltered(updated);

              setAllClients((prev) => {
                const exists = (prev || []).some((c) => c.id === newClient.id);
                if (exists) return prev;
                return [newClient, ...(prev || [])];
              });

              if (newClient.id && selectedDocument) {
                await handleAssignToClients([newClient.id]);
              }
            }
          }}
        />
      )}

      {docToRemove && (
        <ModalComponent
          open={!!docToRemove}
          title="Confirm Removal"
          content={
            <Typography>
              Are you sure you want to remove this assigned document? This action cannot be undone.
            </Typography>
          }
          confirmLabel="Remove"
          onConfirm={() => handleRemoveAssignedDocument(docToRemove.clientId, docToRemove.docId)}
          onCancel={() => !isRemoving && setDocToRemove(null)}
          isLoading={isRemoving}
        />
      )}
    </div>
  </Box>
);
};

export default DocumentsDashboard;